import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle } from 'lucide-react';
import { useServices } from '../../hooks/useServices';
import { supabase } from '../../lib/supabase';

interface ServiceSelectorProps {
  businessId: string;
  selectedId: string;
  staffId?: string;
  onSelect: (serviceId: string) => void;
}

export const ServiceSelector = memo(function ServiceSelector({
  businessId,
  selectedId,
  onSelect
}: ServiceSelectorProps) {
  const { services, loading } = useServices(businessId);

  // Helper to parse duration
  const parseDuration = (duration: string): number => {
    if (!duration) return 30;
    const match = duration.match(/(\d+):(\d+):(\d+)/);
    if (match) {
      const [_, hours, minutes] = match;
      return parseInt(hours) * 60 + parseInt(minutes);
    }
    return 30;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: '#007AFF' }}
        />
      </div>
    );
  }

  if (!services || services.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm" style={{ color: '#8E8E93' }}>לא נמצאו שירותים</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {services.map((service, idx) => {
        const promotion = service.promotion as any;
        const hasPromotion = promotion?.is_active && promotion?.discount_value;

        let displayPrice = service.price;
        if (hasPromotion && promotion) {
          if (promotion.discount_type === 'percentage') {
            displayPrice = service.price * (1 - (promotion.discount_value / 100));
          } else {
            displayPrice = service.price - promotion.discount_value;
          }
        }
        const originalPrice = service.price;
        const duration = parseDuration(service.duration);
        const isSelected = selectedId === service.id;

        return (
          <motion.button
            key={service.id ? service.id : `service-${idx}`}
            whileTap={{ scale: 0.98 }}
            onClick={() => {

              if (!service.id) {

                return;
              }
              onSelect(service.id);
            }}
            className="w-full text-right rounded-2xl overflow-hidden relative"
            style={{
              border: isSelected ? '2px solid #007AFF' : '1px solid #E5E7EB',
              backgroundColor: isSelected ? '#F0F9FF' : '#FFFFFF',
            }}
            type="button"
          >
            {/* Service Image */}
            {service.image_url && (
              <div className="relative h-40 overflow-hidden">
                <img
                  src={service.image_url}
                  alt={service.name_he || service.name || 'שירות'}
                  className="w-full h-full object-cover"
                />
                {isSelected && (
                  <div
                    className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#007AFF' }}
                  >
                    <CheckCircle className="h-5 w-5" style={{ color: '#FFFFFF' }} />
                  </div>
                )}
              </div>
            )}

            {/* Service Details */}
            <div className="p-4">
              <h3 className="font-bold text-lg" style={{ color: '#1C1C1E' }}>
                {service.name_he || service.name || 'שירות ללא שם'}
              </h3>
              {service.description && (
                <p className="text-sm mt-1 line-clamp-2" style={{ color: '#8E8E93' }}>
                  {service.description}
                </p>
              )}
            </div>

          </motion.button>
        );
      })}
    </div>
  );
});
